import React, { useEffect, useRef, useState } from "react";
import { HiOutlineSparkles } from "react-icons/hi2";
import { FiSearch } from "react-icons/fi";
import {
  attachPilotFiles,
  streamPilotAnswer,
  useConfirmPilotChangeMutation,
  useCreatePilotChatMutation,
  useGetPilotChatQuery,
  useRefreshPilotChat,
} from "apis/pilot";
import usePilotSuggestions from "hooks/usePilotSuggestions";
import { showToast } from "components/ui/toast";
import PilotRichText from "./PilotRichText";
import PilotVisual from "./PilotVisual";
import PilotChangeCard from "./PilotChangeCard";
import PilotComposer from "./composer/PilotComposer";
import AddressSuggestions from "./composer/AddressSuggestions";
import PeopleSuggestions from "./composer/PeopleSuggestions";
import PilotFiles from "./PilotFiles";
import PilotLoader from "./PilotLoader";

const EXAMPLE_QUESTIONS = [
  "Which companies signed up this month?",
  "Show failed payments this week",
  "Find the agent named …",
  "How many properties went live last week?",
];

const ACCEPTED_FILES = ".pdf,image/*,audio/*,video/*,.csv,.xls,.xlsx,.doc,.docx";
// The most files one question takes.
const ATTACHMENTS = 5;

// The agent app's Pilot chat, piece by piece (its PilotAnswer): the question on the right, what Pilot is doing while it
// works, and the answer on the left with its tables, charts, cards and changes inside it.

const Question = ({ text, attachments }) => (
  <div className="flex justify-end">
    <div className="max-w-[90%] space-y-1.5 rounded-2xl rounded-br-md bg-black px-3.5 py-2 text-sm text-white">
      {text && <p className="whitespace-pre-wrap">{text}</p>}
      {attachments?.length > 0 && (
        <div className="w-72 max-w-full">
          <PilotFiles files={attachments} />
        </div>
      )}
    </div>
  </div>
);

// One short line that changes as it works, gone once the answer is in.
const ActivityStep = ({ label }) => (
  <p className="flex items-center gap-2 text-sm text-darkgray" aria-live="polite">
    <span className="h-2 w-2 flex-none animate-pulse rounded-full bg-darkgray" aria-hidden="true" />
    <span key={label} className="animate-pulse">
      {label}…
    </span>
  </p>
);

const Answer = ({ message, renderCard }) =>
  (message.text || message.visuals?.length > 0 || message.cards?.length > 0) && (
    <div className="max-w-[90%] space-y-2 rounded-2xl rounded-bl-md bg-lightgray px-3.5 py-2.5">
      <PilotRichText content={message.text} className="whitespace-pre-wrap text-sm text-darkergray" />
      {message.visuals?.map((visual, index) => (
        <div key={index} className="space-y-2">
          <PilotVisual visual={visual} />
        </div>
      ))}
      {message.cards?.map(renderCard)}
    </div>
  );

// The saved messages as turns: each question with the answers after it (a confirmed change's outcome is one more).
const turnsOf = (messages = []) =>
  messages.reduce((turns, message) => {
    if (message.role === "user" || !turns.length) turns.push({ key: message._id, question: null, answers: [] });
    const turn = turns[turns.length - 1];
    if (message.role === "user") turn.question = message;
    else turn.answers.push(message);
    return turns;
  }, []);

// One chat with Pilot: what was said, the answer arriving as it streams, and the box to ask.
// With no chat open, the first question starts one. On a small screen `history` ({ isOpen, onToggle, onClose, panel })
// puts the agent app's clock in the bar, and the chat list in the chat's place while it is open.
export default function PilotConversation({ chatId, onChatStarted, history }) {
  const { data: chat, isPending } = useGetPilotChatQuery(chatId);
  const createChat = useCreatePilotChatMutation();
  const confirm = useConfirmPilotChangeMutation(chatId);
  const refreshChat = useRefreshPilotChat();

  const [input, setInput] = useState("");
  // The suggestions picked for the message being typed: { record, written } with the words written for it.
  const [picked, setPicked] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  // Files attached in the bar: stored as soon as they are picked, sent with the next message (or alone).
  const [files, setFiles] = useState([]);
  const [isAttaching, setIsAttaching] = useState(false);
  // The question being answered: { chatId, question (as typed), attachments, step, text, visuals, cards, error }.
  const [live, setLive] = useState(null);
  const [answerError, setAnswerError] = useState(null);
  const abortRef = useRef(null);
  const answeringChatRef = useRef(null);
  const textareaRef = useRef(null);
  // A change being confirmed is answering too, as on the agent app: the bar and the other cards wait for it.
  const answering = !!live || createChat.isPending || confirm.isPending;

  const changeInput = (text) => {
    setInput(text);
    if (!text.trim()) setPicked([]);
  };

  const pilotSuggestions = usePilotSuggestions({
    text: input,
    isActive: isFocused,
    onPick: (text, record, written) => {
      setInput(text);
      setPicked((current) => [{ record, written }, ...current.filter((one) => one.record.id !== record.id)]);
    },
  });
  const attachFiles = async (chosen) => {
    setIsAttaching(true);
    try {
      const stored = await attachPilotFiles(chosen);
      setFiles((current) => [...current, ...stored]);
    } catch (error) {
      showToast("error", error.response?.data?.message || "Could not attach those files.");
    } finally {
      setIsAttaching(false);
    }
  };

  const showPilotSuggestions = isFocused && pilotSuggestions.suggestions.length > 0;
  const PilotSuggestions = pilotSuggestions.suggestions[0]?.propertyId ? AddressSuggestions : PeopleSuggestions;

  // Opening another chat stops the answer still streaming into this one.
  useEffect(() => {
    if (answeringChatRef.current && answeringChatRef.current !== chatId) abortRef.current?.abort();
  }, [chatId]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Glides to the newest message whenever the chat changes, as the agent app's does.
  const scrollerRef = useRef(null);
  useEffect(() => {
    const el = scrollerRef.current;
    el?.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [chat, live, answerError, confirm.isPending]);

  const patchLive = (patch) => setLive((current) => current && { ...current, ...patch(current) });

  // `typed` is what the admin wrote, shown on the question while it is answered: none for files sent alone.
  const ask = async (question, records, attachments, typed) => {
    setAnswerError(null);
    let askChatId = chatId;
    if (!askChatId) {
      try {
        askChatId = (await createChat.mutateAsync())._id;
      } catch {
        return;
      }
    }
    const controller = new AbortController();
    abortRef.current = controller;
    answeringChatRef.current = askChatId;
    setLive({ chatId: askChatId, question: typed, attachments, step: null, text: "", visuals: [], cards: [] });
    if (askChatId !== chatId) onChatStarted(askChatId);

    try {
      await streamPilotAnswer({
        chatId: askChatId,
        question,
        records,
        attachments,
        signal: controller.signal,
        onEvent: (event, data) => {
          if (event === "step") patchLive(() => ({ step: data.label }));
          else if (event === "text") patchLive((turn) => ({ text: turn.text + data.text }));
          else if (event === "visual") patchLive((turn) => ({ visuals: [...turn.visuals, data] }));
          else if (event === "confirm")
            patchLive((turn) => ({ cards: [...turn.cards, { ...data, status: "waiting" }] }));
          else if (event === "error") {
            patchLive(() => ({ error: data.message }));
            setAnswerError({ chatId: askChatId, message: data.message });
          }
        },
      });
    } catch (error) {
      if (error.name !== "AbortError") setAnswerError({ chatId: askChatId, message: error.message });
    }
    // The saved chat replaces the streamed copy only once it has arrived, so the answer never blinks out.
    await refreshChat(askChatId);
    answeringChatRef.current = null;
    setLive(null);
  };

  // Only the picked records still named in the question go with it. Files sent alone are asked about as the agent
  // app asks: by their names.
  const send = (typed) => {
    if (answering) return;
    // Asking closes the history, as on the agent app.
    history?.onClose();
    const attachments = files.slice(-ATTACHMENTS);
    const question = typed || `Here is what I attached: ${attachments.map((file) => file.name).join(", ")}`;
    const records = picked.filter((one) => question.includes(one.written)).map((one) => one.record);
    setPicked([]);
    setFiles([]);
    ask(question, records, attachments, typed);
  };

  // An example ending in "…" is left for the admin to finish.
  const pickExample = (question) => {
    setInput(question.replace(/…$/, ""));
    textareaRef.current?.focus();
  };
  // The agent app's suggestion chips above its bar: while it is focused and empty, and nothing is being answered.
  const showExamples = isFocused && !input.trim() && !answering;

  const renderCard = (card) => (
    <PilotChangeCard
      key={card.confirmId}
      card={card}
      disabled={answering}
      answering={
        confirm.isPending && confirm.variables?.confirmId === card.confirmId
          ? confirm.variables.cancel
            ? "cancel"
            : "confirm"
          : null
      }
      onAnswer={(cancel) => confirm.mutate({ confirmId: card.confirmId, cancel })}
    />
  );

  const shownLive = live?.chatId === chatId ? live : null;
  // An error the server kept as the answer's words is not said twice.
  const shownError =
    answerError?.chatId === chatId && chat?.messages?.at(-1)?.text !== answerError.message ? answerError.message : null;
  const isEmpty = !chatId && !answering;

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {history?.isOpen ? (
        <div className="flex min-h-0 flex-1 flex-col px-4">{history.panel}</div>
      ) : isEmpty ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
          <HiOutlineSparkles className="h-8 w-8 text-darkgray" aria-hidden="true" />
          <div>
            <h4 className="mb-1">Ask Pilot</h4>
            <p className="text-sm text-darkgray">
              Ask about companies, agents, properties and payments on the platform. Changes wait for you to confirm.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col px-4">
          <div ref={scrollerRef} className="mx-auto min-h-0 w-full max-w-2xl flex-1 space-y-4 overflow-y-auto py-4">
            {chatId && isPending && !shownLive && (
              <div className="flex justify-center py-10">
                <PilotLoader />
              </div>
            )}
            {turnsOf(chat?.messages).map((turn) => (
              <div key={turn.key} className="space-y-2">
                {turn.question && <Question text={turn.question.text} attachments={turn.question.attachments} />}
                {turn.answers.map((answer) => (
                  <Answer key={answer._id} message={answer} renderCard={renderCard} />
                ))}
              </div>
            ))}
            {shownLive && (
              <div className="space-y-2">
                <Question text={shownLive.question} attachments={shownLive.attachments} />
                <ActivityStep label={shownLive.step || "Thinking"} />
                {shownLive.error ? (
                  <p className="text-sm text-red-600">{shownLive.error}</p>
                ) : (
                  <Answer message={shownLive} renderCard={renderCard} />
                )}
              </div>
            )}
            {/* A change being made, as the agent app shows its answer coming. */}
            {confirm.isPending && !shownLive && <ActivityStep label="Thinking" />}
            {shownError && !shownLive && (
              <p role="alert" className="text-sm text-red-600">
                {shownError}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex-none px-6 pb-2 pt-2 lg:px-10">
        <div className="relative mx-auto max-w-2xl">
          {showPilotSuggestions && (
            <PilotSuggestions
              suggestions={pilotSuggestions.suggestions}
              highlighted={pilotSuggestions.highlighted}
              onHighlight={pilotSuggestions.setHighlighted}
              onPick={pilotSuggestions.pick}
            />
          )}
          <PilotComposer
            value={input}
            onChangeText={changeInput}
            onSend={send}
            // The agent app's words while its bar asks Pilot.
            placeholder="Ask me anything or enter an address"
            // A new address, not on the platform yet, while none of the platform's fits and none has been picked.
            suggestAddresses={!showPilotSuggestions && !picked.some((one) => one.record.kind === "property")}
            isSending={answering}
            onStop={live ? () => abortRef.current?.abort() : undefined}
            onOpenHistory={history?.onToggle}
            onFocusChange={setIsFocused}
            inputRef={textareaRef}
            onKeyDown={showPilotSuggestions ? pilotSuggestions.onKeyDown : undefined}
            // The ring only before a chat is open, as the agent app shows it on its dashboard and not over a chat.
            launch={isEmpty && !history?.isOpen}
            onAttachFiles={attachFiles}
            attachments={files}
            onRemoveAttachment={(id) => setFiles((current) => current.filter((file) => file.id !== id))}
            isAttaching={isAttaching}
            acceptFiles={ACCEPTED_FILES}
          >
            {showExamples && (
              <div className="mb-1 flex gap-2 overflow-x-auto pb-1 animate-fade-in">
                {EXAMPLE_QUESTIONS.map((question) => (
                  <button
                    key={question}
                    type="button"
                    // Without this the textarea blurs first and the chips unmount before the click lands.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => pickExample(question)}
                    className="flex shrink-0 items-center gap-1.5 rounded-full border border-mediumgray bg-white px-3 py-1.5 text-darkergray transition hover:bg-lightgray"
                  >
                    <FiSearch size={13} className="text-darkgray" />
                    <span className="text-[13px]">{question}</span>
                  </button>
                ))}
              </div>
            )}
          </PilotComposer>
        </div>
      </div>
    </div>
  );
}
